import React from 'react';
import { ArrowUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  computeZoneLayout,
  SPAN_STYLES,
} from './timeline/TimelinePrimitives';
import { TIMELINE_SEGMENT_HEADING_CLASS } from './timeline/timelineTextTokens';

export interface OffsetBracket {
  flex: number;
  naturalMultiplier: number;
  naturalLabel: string;
}

export interface OffsetLadderEntry {
  multiplier: number;
  label: string;
}

interface OffsetComparisonTimelineProps {
  brackets: OffsetBracket[];
  effectiveLadder: OffsetLadderEntry[];
  offsetIndex: number;
  /**
   * Eyebrow text rendered above the span row.
   * - undefined (default): renders the existing "Surcharge Period" text — preserves current short-turn usage.
   * - string: overrides the text (e.g., "Whole Shift").
   * - null: suppresses the eyebrow entirely (used by count-mode meal-break wrapper).
   */
  headingLabel?: string | null;
}

const MERGED_HEIGHT = 56;
const SPLIT_HALF_HEIGHT = 28;
const SPAN_ROW_HEIGHT = 12;

const CELL_BORDER = 'color-mix(in srgb, var(--border), transparent 50%)';

// Neutral span treatment for contiguous converged (capped) runs — signals
// "this region does not participate in the surcharge lift" without using the
// amber primary accent, which is reserved for the active-surcharge span.
const MUTED_SPAN_STYLE = {
  bg: 'color-mix(in srgb, var(--color-foreground), transparent 97%)',
  border: 'color-mix(in srgb, var(--color-foreground), transparent 92%)',
  labelColor: 'color-mix(in srgb, var(--color-foreground), transparent 70%)',
};

interface SpanRun {
  lifted: boolean;
  leftPct: number;
  rightPct: number;
}

const ZONE_TRANSITION = 'motion-safe:transition-[left,right,width,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]';

export const OffsetComparisonTimeline: React.FC<OffsetComparisonTimelineProps> = ({
  brackets,
  effectiveLadder,
  offsetIndex,
  headingLabel,
}) => {
  // Preserve each bracket's original index so filtering doesn't reindex
  // subsequent cells against the ladder (which would silently mis-classify
  // lifted vs converged).
  const indexedBrackets = (brackets ?? [])
    .map((bracket, originalIndex) => ({ bracket, originalIndex }))
    .filter(({ bracket }) =>
      Number.isFinite(bracket?.flex) && bracket.flex > 0 &&
      Number.isFinite(bracket?.naturalMultiplier) && bracket.naturalMultiplier >= 0,
    );
  // Ladder is used directly by originalIndex — any invalid entry would shift
  // indices, so we validate all-or-nothing.
  const effectiveLadderArr = effectiveLadder ?? [];
  const ladderAllValid = effectiveLadderArr.every(e =>
    Number.isFinite(e?.multiplier) && e.multiplier >= 0,
  );
  const safeOffset = Number.isFinite(offsetIndex) ? Math.max(1, Math.floor(offsetIndex)) : NaN;

  if (
    indexedBrackets.length < 2 ||
    !ladderAllValid ||
    effectiveLadderArr.length < (brackets ?? []).length ||
    !Number.isFinite(safeOffset)
  ) {
    return null;
  }

  const effectiveMinPct = Math.min(22, Math.floor(95 / indexedBrackets.length));
  const { widths } = computeZoneLayout(
    indexedBrackets.map(({ bracket }) => ({ flex: bracket.flex })),
    { minPct: effectiveMinPct },
  );

  const cells = indexedBrackets.map(({ bracket, originalIndex }, i) => {
    const destIndex = Math.min(originalIndex + safeOffset, effectiveLadderArr.length - 1);
    const dest = effectiveLadderArr[destIndex];
    return {
      naturalLabel: bracket.naturalLabel,
      destLabel: dest.label,
      lifted: dest.multiplier > bracket.naturalMultiplier,
      width: widths[i],
    };
  });

  // Group contiguous cells by lifted state so each run can render its own
  // span (amber "Surcharge" for lifted, muted "No Surcharge" for capped).
  const runs: SpanRun[] = [];
  let cumPct = 0;
  for (let i = 0; i < cells.length; i++) {
    const prevRun = runs[runs.length - 1];
    const segEnd = cumPct + cells[i].width;
    if (prevRun && prevRun.lifted === cells[i].lifted) {
      prevRun.rightPct = 100 - segEnd;
    } else {
      runs.push({ lifted: cells[i].lifted, leftPct: cumPct, rightPct: 100 - segEnd });
    }
    cumPct = segEnd;
  }

  return (
    <div
      role="img"
      aria-label={`Offset overtime: every natural tier shifted up by ${safeOffset} step${safeOffset === 1 ? '' : 's'}`}
    >
      <div className="flex flex-col min-w-0">
        {headingLabel !== null && (
          <div className="text-center leading-none mb-2">
            <span className="block font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.5px] text-muted-foreground/60">
              {headingLabel ?? 'Surcharge Period'}
            </span>
          </div>
        )}

        <div className="relative" style={{ height: SPAN_ROW_HEIGHT }}>
          {runs.map((run, i) => {
            const style = run.lifted ? SPAN_STYLES.v1 : MUTED_SPAN_STYLE;
            const label = run.lifted ? 'Surcharge' : 'No Surcharge';
            return (
              <div
                key={i}
                className={cn('absolute top-0 h-full flex items-center justify-center rounded-t-[0.25rem] overflow-hidden', ZONE_TRANSITION)}
                style={{
                  left: `${run.leftPct}%`,
                  right: `${run.rightPct}%`,
                  background: style.bg,
                  borderTop: `1px solid ${style.border}`,
                  borderLeft: `1px solid ${style.border}`,
                  borderRight: `1px solid ${style.border}`,
                }}
              >
                <span
                  className="font-heading uppercase font-semibold whitespace-nowrap text-[10px] min-[375px]:text-[11px] sm:text-[12px] leading-none tracking-[0.75px] sm:tracking-[1px]"
                  style={{ color: style.labelColor }}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>

        <div className="flex items-stretch">
          {cells.map((cell, i) => {
            const isFirst = i === 0;
            const isLast = i === cells.length - 1;
            const prevSameType = i > 0 && cells[i - 1].lifted === cell.lifted;
            // Border drawn with inset box-shadow instead of `border` so it does
            // not consume box-model space (preserves the 56/28/28 dimensional
            // invariant). When the previous cell is the same type we omit the
            // left edge so adjacent same-type cells appear merged.
            const cellBorderShadow = prevSameType
              ? `inset -1px 0 0 0 ${CELL_BORDER}, inset 0 1px 0 0 ${CELL_BORDER}, inset 0 -1px 0 0 ${CELL_BORDER}`
              : `inset 0 0 0 1px ${CELL_BORDER}`;

            // Both lifted and converged branches are always rendered, absolutely
            // positioned at inset:0. Opacity crossfades between them when
            // `cell.lifted` flips (e.g. adding/removing an Extended OT tier).
            // Rendering both structures keeps DOM identity stable so CSS
            // transitions can run instead of React swapping the subtree.
            return (
              <div
                key={i}
                className={cn(
                  'relative overflow-hidden',
                  isFirst && 'rounded-l-[0.25rem]',
                  isLast && 'rounded-r-[0.25rem]',
                  ZONE_TRANSITION,
                )}
                style={{ width: `${cell.width}%`, height: MERGED_HEIGHT, boxShadow: cellBorderShadow }}
              >
                {/* Lifted branch (split cell) */}
                <div
                  aria-hidden={!cell.lifted}
                  className={cn(
                    'absolute inset-0 flex flex-col',
                    'motion-safe:transition-opacity motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]',
                  )}
                  style={{ opacity: cell.lifted ? 1 : 0, pointerEvents: cell.lifted ? 'auto' : 'none' }}
                >
                  <div
                    className="flex items-center justify-center bg-primary/[0.15]"
                    style={{
                      height: SPLIT_HALF_HEIGHT,
                      boxShadow: 'inset 0 0 12px color-mix(in srgb, var(--primary), transparent 92%)',
                    }}
                  >
                    <span className="relative inline-flex items-center justify-center">
                      <span className={cn(TIMELINE_SEGMENT_HEADING_CLASS, 'text-primary-text/95')}>
                        {cell.destLabel}
                      </span>
                      <ArrowUp
                        aria-hidden="true"
                        className="pointer-events-none absolute right-full mr-1 text-primary-text/60 w-[10px] h-[10px] sm:w-3 sm:h-3"
                      />
                    </span>
                  </div>
                  <div style={{ height: 0, borderTop: '1px dashed color-mix(in srgb, var(--primary), transparent 55%)' }} />
                  <div
                    className="flex items-center justify-center bg-foreground/[0.04]"
                    style={{ height: SPLIT_HALF_HEIGHT }}
                  >
                    <span className={cn(TIMELINE_SEGMENT_HEADING_CLASS, 'text-foreground/55 line-through')}>
                      {cell.naturalLabel}
                    </span>
                  </div>
                </div>

                {/* Converged branch (flat cell) — destination equals natural
                    (only possible when the offset would have lifted past the
                    ladder top, so the destination is clamped). Small "CAPPED"
                    caption below the rate makes the cap state explicit. */}
                <div
                  aria-hidden={cell.lifted}
                  className={cn(
                    'absolute inset-0 flex flex-col items-center justify-center gap-1 bg-primary/[0.10]',
                    'motion-safe:transition-opacity motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]',
                  )}
                  style={{ opacity: cell.lifted ? 0 : 1, pointerEvents: cell.lifted ? 'none' : 'auto' }}
                >
                  <span className={cn(TIMELINE_SEGMENT_HEADING_CLASS, 'text-primary-text/85')}>
                    {cell.destLabel}
                  </span>
                  <span className="font-label text-[8px] sm:text-[9px] leading-none uppercase tracking-[0.75px] text-muted-foreground/65">
                    Capped
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
